import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import ApiService from '../services/api';
import { ClassroomsWithRatios, ClassroomWithRatio, SectionRatio } from '../types';
import toast from 'react-hot-toast';

/**
 * Evaluates the staffing status of a classroom based on its student-to-teacher ratio.
 * 
 * @param students - Number of students in the classroom
 * @param teachers - Number of active teachers in the classroom
 * @param requiredRatio - Target ratio based on classroom configuration
 * @returns 'under' if understaffed, 'over' if overstaffed, or 'optimal' if properly staffed
 */
const determineRatioStatus = (students: number, teachers: number, requiredRatio: number): 'under' | 'optimal' | 'over' => {
  if (students === 0 && teachers === 0) return 'optimal';
  
  // Calculate the minimum number of teachers required based on the required ratio
  const requiredTeachers = Math.ceil(students / requiredRatio);
  
  if (teachers < requiredTeachers) {
    return 'under';
  } else if (teachers > requiredTeachers) {
    return 'over';
  } else {
    return 'optimal';
  }
};

// Get refresh interval from environment variable or use default (60 seconds)
const REFRESH_INTERVAL = parseInt(import.meta.env.VITE_REFRESH_INTERVAL || '60000', 10);

export function useClassroomData(options?: { autoRefresh?: boolean }) {
  const autoRefresh = options?.autoRefresh ?? true;
  
  return useQuery({
    queryKey: ['classroomRatios'],
    queryFn: async () => {
      toast.loading('Refreshing data...', {
        id: 'queryLoading',
        duration: Infinity,
      })

      try {
        const data = Object.entries(await ApiService.getClassroomRatios()).reduce((acc, [classId, classroom]) => {
          let ratio = classroom.teacherCount > 0 ? classroom.studentCount / classroom.teacherCount : Infinity;
          let displayRatio = classroom.teacherCount > 0 ? `${(classroom.studentCount / classroom.teacherCount).toFixed(1)} : 1` : 'N/A';
          let ratioStatus: 'under' | 'optimal' | 'over' = determineRatioStatus(classroom.studentCount, classroom.activeTeacherCount, classroom.requiredRatio);

          const extraTeachers = Math.ceil(classroom.studentCount / classroom.requiredRatio) - classroom.activeTeacherCount;
          let statusText = extraTeachers === 0
            ? ""
            : `${Math.abs(extraTeachers)} ${Math.abs(extraTeachers) === 1 ? 'teacher' : 'teachers'} ${extraTeachers > 0 ? 'needed' : 'over'}`;

          if (classroom.section.toLocaleLowerCase() === 'other' || classroom.id.toLocaleLowerCase() === 'unassigned') {
            ratio = 0;
            displayRatio = 'N/A';
            ratioStatus = 'optimal',
            statusText = ''
            classroom.requiredRatio = 0;
          }

          acc[classId] = {
            ...classroom,
            ratio,
            displayRatio,
            ratioStatus,
            statusText,
          };
          return acc;
        }, {} as ClassroomsWithRatios);
        
        return data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        throw new Error(errorMessage);
      }
    },
    refetchInterval: autoRefresh ? REFRESH_INTERVAL : undefined,
    staleTime: 1*60*1000 // reply on referchInterval to keep data fresh
  });
}

export function processedClassroomData(classroomsData: ClassroomsWithRatios | undefined) {
  if (!classroomsData) {
    return { sections: [], specialCategories: [], classroomOptions: [] };
  }
  
  const classroomArray: ClassroomWithRatio[] = Object.values(classroomsData);
  
  // Process data for special categories
  const specialCategories = classroomArray.filter(c => c.id === 'office' || c.id === 'unassigned');
  
  // Group classrooms by section
  const sectionMap: Record<string, ClassroomWithRatio[]> = {};
  classroomArray.forEach(classroom => {
    // Skip special categories
    if (classroom.id === 'office' || classroom.id === 'unassigned') return;
    
    if (!sectionMap[classroom.section]) {
      sectionMap[classroom.section] = [];
    }
    sectionMap[classroom.section].push(classroom);
  });
  
  // Calculate section-level ratios and create sections array
  const sections: SectionRatio[] = Object.entries(sectionMap).map(([name, classrooms]) => {
    const totalStudents = classrooms.reduce((sum, c) => sum + c.studentCount, 0);
    const totalActiveTeachers = classrooms.reduce((sum, c) => sum + c.activeTeacherCount, 0);
    const ratio = totalStudents === 0 
      ? 0 
      : totalActiveTeachers === 0 
        ? Infinity 
        : totalStudents / totalActiveTeachers;
    
    // Calculate weighted average required ratio for the section
    let totalWeightedRequiredRatio = Math.min(...classrooms.map(classroom => classroom.requiredRatio));
    
    // Calculate the minimum number of teachers required based on the required ratio
    const requiredTeachers = totalStudents > 0 ? Math.ceil(totalStudents / totalWeightedRequiredRatio) : 0;
    
    // Determine section overall status
    let sectionStatus;    
    if (totalStudents === 0 && totalActiveTeachers === 0) sectionStatus = 'optimal';
    else if (totalActiveTeachers < requiredTeachers) {
      sectionStatus = 'under';
    } else if (totalActiveTeachers > requiredTeachers) {
      sectionStatus = 'over';
    } else {
      sectionStatus = 'optimal';
    }

    const extraTeachers = requiredTeachers - totalActiveTeachers;
    let statusText = extraTeachers === 0
      ? ""
      : `${Math.abs(extraTeachers)} ${Math.abs(extraTeachers) === 1 ? 'teacher' : 'teachers'} ${extraTeachers > 0 ? 'needed' : 'over'}`;

    if (name.toLowerCase() === 'other'){
      sectionStatus = 'optimal';
      statusText = '';
      totalWeightedRequiredRatio = 0;
    }

    return {
      name,
      totalStudents,
      totalActiveTeachers,
      ratio,
      requiredRatio: totalWeightedRequiredRatio,
      displayRatio: ratio === Infinity ? '∞' : `${(totalStudents/totalActiveTeachers).toFixed(1)} : 1`,
      ratioStatus: sectionStatus as 'under' | 'optimal' | 'over',
      statusText,
      classrooms: classrooms.sort((a, b) => {
        // Sort by classroom.order if available, fallback to name for alphabetical sorting
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return a.name.localeCompare(b.name);
      })
    };
  }).sort((a, b) => {
    // Custom sort order: infant, toddler, pre-school, pre-k
    const getSectionPriority = (name: string): number => {
      const lowerName = name.toLowerCase();
      if (lowerName.includes('infant')) return 1;
      if (lowerName.includes('toddler')) return 2;
      if (lowerName.includes('pre-school') || lowerName.includes('preschool')) return 3;
      if (lowerName.includes('pre-k') || lowerName.includes('prek')) return 4;
      if (lowerName.includes('school')) return 5;
      return 100; // For any other sections
    };
    
    const priorityA = getSectionPriority(a.name);
    const priorityB = getSectionPriority(b.name);
    
    // Sort by priority first, then alphabetically if priorities are the same
    return priorityA !== priorityB 
      ? priorityA - priorityB
      : a.name.localeCompare(b.name);
  });
  
  // Extract classroom options for check-in dropdown
  const classroomOptions: ClassroomWithRatio[] = Object.values(classroomsData)
    .filter(classroom => 
      // Exclude special categories like office and unassigned
      classroom.id !== 'office' && classroom.id !== 'unassigned'
    ).sort((a, b) => a.name.localeCompare(b.name));
  
  return { sections, specialCategories, classroomOptions };
}

// Add a hook to check if any classroom is understaffed
export function understaffedStatus(classroomsData: ClassroomsWithRatios | undefined) {
  if (!classroomsData) return false;
  
  return Object.values(classroomsData).some(classroom => 
    classroom.id !== 'office' && 
    classroom.id !== 'unassigned' && 
    classroom.ratioStatus === 'under'
  );
}

// Add a hook for summary statistics
export function summaryStats(sections: SectionRatio[], classroomsData: ClassroomsWithRatios | undefined) {
  if (!sections.length) {
    return { totalStudents: 0, totalActiveTeachers: 0, totalTeachersOnBreak: 0, totalTeachers: 0 };
  }
  
  const totalStudents = sections.reduce((sum, section) => sum + section.totalStudents, 0);
  const totalActiveTeachers = sections.reduce((sum, section) => sum + section.totalActiveTeachers, 0);
  
  // Calculate teachers on break and total teachers from classroomsInfo
  let totalTeachersOnBreak = 0;
  let totalTeachers = 0;
  
  if (classroomsData) {
    Object.values(classroomsData).forEach(classroom => {
      if (classroom.id !== 'office' && classroom.id !== 'unassigned') {
        totalTeachersOnBreak += (classroom.teacherCount - classroom.activeTeacherCount);
        totalTeachers += classroom.teacherCount;
      }
    });
  }
  
  return { totalStudents, totalActiveTeachers, totalTeachersOnBreak, totalTeachers };
}
